import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useEmployeeAuth } from './EmployeePortalLayout';
import {
  TrendingUp,
  Star,
  Briefcase,
  Award,
  ThumbsUp,
  BarChart3
} from 'lucide-react';

export const EmployeePerformance = () => {
  const { employee, api } = useEmployeeAuth();
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPerformance();
  }, []);

  const fetchPerformance = async () => {
    try {
      const response = await api.get('/api/employee-portal/my-performance');
      setPerformance(response.data);
    } catch (error) {
      console.error('Error fetching performance:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRatingBar = (value, max = 5) => {
    const percentage = (value / max) * 100;
    return (
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className="h-full bg-[#C6A87C] rounded-full transition-all"
          style={{ width: `${percentage}%` }}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold">My Performance</h1>
        <p className="text-gray-500 text-sm">Your ratings and customer feedback</p>
      </div>

      {/* Overall Grade Card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d] text-white rounded-2xl p-6"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white/70 text-sm">Overall Grade</p>
            <p className="text-5xl font-bold mt-2">{performance?.letter_grade || 'N/A'}</p>
            <p className="text-[#C6A87C] mt-1">
              Score: {performance?.overall_grade?.toFixed(1) || 'N/A'}/100
            </p>
          </div>
          <div className="w-20 h-20 rounded-full border-4 border-[#C6A87C] flex items-center justify-center">
            <TrendingUp className="w-10 h-10 text-[#C6A87C]" />
          </div>
        </div>
      </motion.div>

      {/* Performance Ratings */}
      {performance?.current_ratings && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl p-4 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-[#C6A87C]" />
            <h2 className="font-semibold">Performance Ratings</h2>
          </div>
          
          <div className="space-y-4">
            {[
              { key: 'speed', label: 'Speed', icon: '⚡' },
              { key: 'accuracy', label: 'Accuracy', icon: '🎯' },
              { key: 'quality', label: 'Quality', icon: '✨' },
              { key: 'reliability', label: 'Reliability', icon: '🔒' },
              { key: 'teamwork', label: 'Teamwork', icon: '🤝' },
              { key: 'communication', label: 'Communication', icon: '💬' },
            ].map((rating) => (
              <div key={rating.key}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-700">
                    {rating.icon} {rating.label}
                  </span>
                  <span className="font-semibold">
                    {performance.current_ratings[rating.key]?.toFixed(1) || 'N/A'}
                  </span>
                </div>
                {getRatingBar(performance.current_ratings[rating.key] || 0)}
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Customer Satisfaction */}
      {performance?.customer_satisfaction && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white rounded-2xl p-4 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-4">
            <Star className="w-5 h-5 text-[#C6A87C]" />
            <h2 className="font-semibold">Customer Satisfaction</h2>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-[#C6A87C]/10 rounded-xl p-4 text-center">
              <div className="flex justify-center gap-1 mb-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-4 h-4 ${
                      star <= Math.round(performance.customer_satisfaction.average_rating)
                        ? 'fill-[#C6A87C] text-[#C6A87C]'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <p className="text-2xl font-bold text-[#C6A87C]">
                {performance.customer_satisfaction.average_rating}
              </p>
              <p className="text-xs text-gray-500">Avg Rating</p>
            </div>
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <ThumbsUp className="w-6 h-6 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-green-600">
                {performance.customer_satisfaction.would_recommend_percentage}%
              </p>
              <p className="text-xs text-gray-500">Would Recommend</p>
            </div>
          </div>
          
          <p className="text-sm text-gray-500 text-center">
            Based on {performance.customer_satisfaction.total_reviews} customer reviews from your jobs
          </p>
        </motion.div>
      )}

      {/* Past Jobs Count */}
      {performance?.past_jobs_count !== undefined && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl p-4 shadow-sm"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
              <Briefcase className="w-7 h-7 text-blue-600" />
            </div>
            <div>
              <p className="text-3xl font-bold">{performance.past_jobs_count}</p>
              <p className="text-sm text-gray-500">Completed Jobs</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Certifications */}
      {employee.certifications && employee.certifications.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white rounded-2xl p-4 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-5 h-5 text-[#C6A87C]" />
            <h2 className="font-semibold">Your Certifications</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {employee.certifications.map((cert, i) => (
              <span
                key={i}
                className="px-3 py-2 bg-green-50 text-green-700 text-sm rounded-full"
              >
                {cert}
              </span>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default EmployeePerformance;
